/*
 * <rf-initiales> — saisie et validation des initiales + aperçu en direct.
 *
 * - La liste des caractères autorisés vient du réglage du bloc (data-allowed).
 * - Le bouton « Ajouter au panier » reste désactivé (aria-disabled + clic bloqué) tant que le
 *   champ est vide ou invalide. On ne touche pas à l'attribut disabled, réservé au thème
 *   (variante épuisée). Un MutationObserver réapplique le verrou quand le thème rerend le bouton.
 * - En dernier filet de sécurité, l'envoi du formulaire est bloqué si les initiales sont invalides.
 */

const SEPARATORS = /[\s,]+/;

/**
 * Forme du module caméra d'un modèle d'iPhone (même correspondance que snippets/module-camera.liquid).
 * @param {string} model
 */
function cameraFor(model) {
  if (model.includes('Air')) return 'air';
  if (model.includes('17 Pro')) return 'plateau';
  if (model.includes('Pro')) return 'tri';
  if (model.includes('16') || model.includes('17')) return 'pill';
  return 'diag';
}

class RfInitiales extends HTMLElement {
  /** @type {HTMLInputElement} */ #input;
  /** @type {HTMLElement | null} */ #error;
  /** @type {HTMLElement | null} */ #count;
  /** @type {SVGTextElement | null} */ #text;
  /** @type {SVGRectElement | null} */ #case;
  /** @type {Set<string>} */ #allowed = new Set();
  /** @type {Record<string, string>} */ #caseColors = {};
  /** @type {HTMLFormElement | null} */ #form = null;
  /** @type {MutationObserver | null} */ #observer = null;
  #touched = false;
  #shown = 0;
  #max = 4;
  #placeholder = '';

  connectedCallback() {
    this.#input = /** @type {HTMLInputElement} */ (this.querySelector('[data-input]'));
    this.#error = this.querySelector('[data-error]');
    this.#count = this.querySelector('[data-count]');
    this.#text = this.querySelector('.rf-initiales__text');
    this.#case = this.querySelector('.rf-initiales__case');
    this.#placeholder = this.#text?.textContent?.trim() ?? '';
    this.#max = Number(this.dataset.max) || 4;

    for (const token of (this.dataset.allowed || '').split(SEPARATORS)) {
      for (const char of Array.from(token)) this.#allowed.add(char);
    }

    try {
      const json = this.querySelector('[data-case-colors]')?.textContent;
      if (json) this.#caseColors = JSON.parse(json);
    } catch (e) {
      this.#caseColors = {};
    }

    this.#input.addEventListener('input', this.#onInput);
    this.#input.addEventListener('blur', this.#onBlur);
    this.addEventListener('change', this.#onFinishChange);
    document.addEventListener('change', this.#onVariantChange);
    document.addEventListener('submit', this.#onSubmit, true);
    document.addEventListener('click', this.#onClick, true);

    this.#form = /** @type {HTMLFormElement | null} */ (document.getElementById(this.dataset.formId || ''));
    if (this.#form) {
      this.#observer = new MutationObserver(this.#onFormMutation);
      this.#observer.observe(this.#form, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-disabled', 'data-rf-locked'] });
    }

    // Initiales tapées sur l'accueil (?initiales=CARO) : on préremplit le champ.
    const fromUrl = new URLSearchParams(window.location.search).get('initiales');
    if (fromUrl && !this.#input.value) {
      this.#input.value = Array.from(fromUrl).slice(0, this.#max).join('');
      this.#touched = true;
    }

    this.#initSticky();
    this.#update();
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('input', this.#onInput);
    this.#input?.removeEventListener('blur', this.#onBlur);
    this.removeEventListener('change', this.#onFinishChange);
    document.removeEventListener('change', this.#onVariantChange);
    document.removeEventListener('submit', this.#onSubmit, true);
    document.removeEventListener('click', this.#onClick, true);
    this.#observer?.disconnect();
    this.#stickyObserver?.disconnect();
  }

  /** @type {HTMLElement | null} */ #sticky = null;
  /** @type {IntersectionObserver | null} */ #stickyObserver = null;

  /**
   * Barre d'achat collante (mobile) : visible quand ni le champ ni le bouton d'achat ne sont à l'écran.
   * Sans initiales valides, elle amène au champ ; sinon, elle déclenche le vrai bouton d'ajout au panier.
   */
  #initSticky() {
    this.#sticky = this.querySelector('[data-sticky]');
    if (!this.#sticky || !('IntersectionObserver' in window)) return;
    // Hors du bloc pour échapper aux conteneurs transformés ou collants.
    document.body.appendChild(this.#sticky);

    const visible = new Set();
    this.#stickyObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      }
      if (this.#sticky) this.#sticky.hidden = visible.size > 0;
    });
    this.#stickyObserver.observe(this);
    const buyButtons = this.#form?.closest('.buy-buttons-block') || this.#form;
    if (buyButtons) this.#stickyObserver.observe(buyButtons);

    this.#sticky.querySelector('[data-sticky-button]')?.addEventListener('click', () => {
      if (this.validate().valid) {
        /** @type {HTMLButtonElement | null} */ (this.#form?.querySelector('.add-to-cart-button'))?.click();
      } else {
        this.#touched = true;
        this.#update();
        this.#input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.#input.focus({ preventScroll: true });
      }
    });
  }

  #renderSticky() {
    if (!this.#sticky) return;
    const button = this.#sticky.querySelector('[data-sticky-button]');
    if (button instanceof HTMLElement) {
      button.textContent = this.validate().valid ? button.dataset.labelReady || '' : button.dataset.labelEmpty || '';
    }
    // Prix de la variante sélectionnée, repris du bloc prix de la fiche.
    const price = document.querySelector('.product-details .price');
    const target = this.#sticky.querySelector('[data-sticky-price]');
    if (price?.textContent && target) target.textContent = price.textContent.trim();
  }

  /** @returns {{ valid: boolean, message: string }} */
  validate() {
    const chars = Array.from(this.#input.value);

    if (chars.length === 0) {
      return { valid: false, message: this.dataset.msgEmpty || '' };
    }

    if (chars.length > this.#max) {
      const msg = (this.dataset.msgTooLong || '[max] caractères maximum.').replace('[max]', String(this.#max));
      return { valid: false, message: msg };
    }

    const refused = [...new Set(chars.filter((c) => !this.#allowed.has(c)))];
    if (refused.length > 0) {
      const list = refused.map((c) => (c === ' ' ? '« espace »' : `« ${c} »`)).join(', ');
      let msg = (this.dataset.msgInvalid || '[caracteres] n’est pas disponible.').replace('[caracteres]', list);
      if (refused.length > 1) msg = msg.replace(/n[’']est pas disponible/, 'ne sont pas disponibles');
      return { valid: false, message: msg };
    }

    return { valid: true, message: '' };
  }

  #onInput = () => {
    this.#touched = true;
    this.#update();
  };

  #onBlur = () => {
    this.#touched = true;
    this.#update();
  };

  /** @param {Event} event */
  #onFinishChange = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (target?.dataset?.finish) this.#renderPreview();
  };

  /** Aperçu : couleur de la coque = coloris sélectionné, module caméra = modèle sélectionné. */
  /** @param {Event} event */
  #onVariantChange = (event) => {
    // Le prix de la nouvelle variante arrive après le rerendu du thème.
    window.setTimeout(() => this.#renderSticky(), 900);
    const colorOption = this.dataset.colorOption;
    if (!this.#case) return;
    const target = /** @type {HTMLInputElement | HTMLSelectElement} */ (event.target);
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    let optionName = target.dataset.optionName || '';
    let value = target.value;
    if (target instanceof HTMLSelectElement) {
      const selected = target.selectedOptions[0];
      optionName = selected?.dataset.optionName || target.name || '';
      value = selected?.value || value;
    }
    const modelOption = this.dataset.modelOption;
    if (modelOption && optionName.startsWith(modelOption)) {
      this.#renderModel(value);
      return;
    }
    if (!colorOption || !optionName.startsWith(colorOption)) return;

    const hex = this.#caseColors[value];
    if (hex) this.#case.setAttribute('fill', hex);
  };

  /** @param {string} model */
  #renderModel(model) {
    const cam = cameraFor(model);
    for (const group of this.querySelectorAll('[data-cam]')) {
      group.classList.toggle('is-active', group.getAttribute('data-cam') === cam);
    }
    const label = this.querySelector('[data-model-label]');
    if (label) label.textContent = model;
  }

  /** Filet de sécurité : bloque l'envoi du formulaire produit si les initiales sont invalides. */
  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (event.target !== this.#form) return;
    const { valid } = this.validate();
    if (valid) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#touched = true;
    this.#update();
    this.#input.focus();
  };

  // Le thème rerend le bouton lors d'un changement de variante : on réapplique notre verrou.
  #onFormMutation = () => {
    this.#syncButtons();
    this.#renderSticky();
  };

  /** Clic sur un bouton verrouillé : on bloque et on explique pourquoi. */
  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-rf-locked="true"]');
    if (!button || !this.#form?.contains(button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#touched = true;
    this.#update();
    this.#input.focus();
  };

  #update() {
    const { valid, message } = this.validate();
    const length = Array.from(this.#input.value).length;

    if (this.#count) this.#count.textContent = `${length}/${this.#max}`;

    // Le message « champ vide » ne s'affiche qu'après une première interaction.
    const showError = !valid && (this.#touched || length > 0) && message;
    if (this.#error) {
      this.#error.hidden = !showError;
      this.#error.textContent = showError ? message : '';
    }
    this.#input.setAttribute('aria-invalid', String(Boolean(showError)));
    this.#input.setCustomValidity(valid ? '' : message || 'Initiales invalides');

    this.#renderPreview();
    this.#syncButtons();
    this.#renderSticky();
  }

  #renderPreview() {
    if (!this.#text) return;
    const value = this.#input.value;
    const isEmpty = value.length === 0;
    // Dorure à chaud : à chaque lettre ajoutée, les initiales sont « frappées » (CSS .is-hit).
    if (value.length > this.#shown) {
      this.#text.classList.remove('is-hit');
      void this.#text.getBoundingClientRect();
      this.#text.classList.add('is-hit');
    }
    this.#shown = value.length;
    this.#text.textContent = isEmpty ? this.#placeholder : value;
    this.#text.classList.toggle('is-placeholder', isEmpty);

    const finish = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-finish]:checked'));
    const fill = finish?.dataset.finish === 'silver' ? this.#text.dataset.silver : this.#text.dataset.gold;
    if (fill) this.#text.setAttribute('fill', fill);
  }

  /** Verrouille visuellement (aria-disabled) les boutons d'ajout tant que les initiales sont invalides. */
  #syncButtons() {
    if (!this.#form) return;
    const { valid } = this.validate();
    const buttons = /** @type {NodeListOf<HTMLButtonElement>} */ (
      this.#form.querySelectorAll('button[type="submit"], .add-to-cart-button')
    );

    for (const button of buttons) {
      if (!valid) {
        if (button.dataset.rfLocked !== 'true') button.dataset.rfLocked = 'true';
        if (button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.rfLocked === 'true') {
        delete button.dataset.rfLocked;
        button.removeAttribute('aria-disabled');
      }
    }

    // Ignore les mutations que nous venons de provoquer.
    this.#observer?.takeRecords();
  }
}

if (!customElements.get('rf-initiales')) customElements.define('rf-initiales', RfInitiales);
